import isBlank from "@sedan-utils/is-blank";
import { Meta } from "../../cutil/Meta";
import { StoryNodeType } from "../../storyV2/domain/StoryNodeType";
import { FuncResponseType, RunStreamResponse, } from "../../storyV2/function/FunctionBase";
import { FunctionName } from "../../storyV2/function/FunctionRegistry";
import { FunctionUtil } from "../../storyV2/function/FunctionUtil";
import { tryPromise, TryResult } from "../../cutil/LangUtil";
import { EdgeState } from "../../storyV2/domain/StoryEdge";
import { AdapterErrorUtil } from "../../storyV2/adapter/AdapterErrorUtil";
import { AdapterResponseUtil } from "../../storyV2/adapter/AdapterResponseUtil";
import { ImageModel } from "../../storyV2/model/ImageModel";
import { JsonUtil } from "../../cutil/JsonUtil";
import { FunctionParamsManager } from "../function/FunctionParams";
export class AdapterBase {
    constructor(funcRegistry) {
        this.funcRegistry = funcRegistry;
    }
    async run(story, edge, sourceNode, actionData) {
        if (sourceNode === undefined) {
            return Promise.reject();
        }
        await this.globalContextUpdateQuery(story, sourceNode, edge);
        switch (sourceNode.options.type) {
            case StoryNodeType.CUSTOMGROUP:
                const children = sourceNode.children ?? [];
                const edgeStreamResponse = new RunStreamResponse();
                this.runGroup(story, edge, children, actionData, edgeStreamResponse);
                return new RunResponse(this.dynamicRunResponseType(), edgeStreamResponse);
            case StoryNodeType.CUSTOMNODE:
                const res = await tryPromise(this.runNode(sourceNode, actionData, story, edge));
                if (res.error) {
                    return new RunResponse(RunResponseType.ERROR, AdapterErrorUtil.getErrorMsg(res));
                }
                return new RunResponse(this.dynamicRunResponseType(), res.data);
        }
    }
    dynamicRunResponseType() {
        if (this.lastFuncResType) {
            const resType = AdapterResponseUtil.responseTypeTransform(this.lastFuncResType);
            return resType;
        }
        return this.runResponseType();
    }
    runResponseType() {
        throw new Meta("Undefined RunResponseType", `Undefined RunResponseType`, "");
    }
    async runNode(node, actionData, story, edge) {
        const query = node.content;
        if (isBlank(query)) {
            return Promise.reject();
        }
        if (this.functionName) {
            actionData.funcOriginalData.functionName = this.functionName;
        }
        const context = {
            isNested: story.isNested,
        };
        const requestParams = FunctionParamsManager.getByType(this.functionName).getParams(actionData, node, context);
        const funcResponse = await FunctionUtil.findFuncAndRun(query, actionData, this.funcRegistry, FunctionUtil.createFuncVariables(story, edge), {
            projectId: story.projectId,
            sourceNodeId: node.options.id,
            edgeId: edge.options.id,
            templateId: story.templateId,
        }, () => edge.state === EdgeState.ERROR, node, requestParams);
        this.lastFuncResType = funcResponse.type;
        const packageData = this.responseDataPackage(isBlank(node.originContent) ? node.content : node.originContent, funcResponse);
        return packageData;
    }
    responseDataPackage(input, funcResponse) {
        switch (funcResponse.type) {
            case FuncResponseType.YOUTUBE_VIDEOS:
                const data = funcResponse.data;
                if (data instanceof Object && data.searchKey !== undefined) {
                    // use answer field as searchKey if input is from ask brain
                    const inputJson = JsonUtil.parse(input);
                    if (inputJson !== undefined && inputJson.answer !== undefined) {
                        data.searchKey = inputJson.answer;
                    }
                    else {
                        data.searchKey = input;
                    }
                }
                return data;
            case FuncResponseType.IMAGE_TEXT:
                return new ImageModel(input, funcResponse.data);
        }
        return funcResponse.data;
    }
    runGroup(story, edge, nodes, actionData, edgeStreamResponse) {
        const map = new Map();
        const requestArr = nodes.map((node, index) => {
            const request = this.runNode(node, actionData, story, edge)
                .then(res => {
                map.set(index, new TryResult(res, undefined));
                let completion = false;
                if (map.size === nodes.length) {
                    completion = true;
                }
                edgeStreamResponse.receive(map, completion);
                return Promise.resolve();
            })
                .catch(error => {
                map.set(index, new TryResult(undefined, error));
                let completion = false;
                if (map.size === nodes.length) {
                    completion = true;
                }
                edgeStreamResponse.receive(map, completion);
                return Promise.resolve();
            });
            return request;
        });
        return Promise.all(requestArr).then(() => {
            return Promise.resolve();
        });
    }
    async globalContextUpdateQuery(story, sourceNode, edge) {
        const globalContextFunc = this.funcRegistry.getByRegistName(FunctionName.globalContext);
        return globalContextFunc.updateSourceContentForStory(story, sourceNode, edge);
    }
}
export class CustomAdapterBase extends AdapterBase {
}
export var RunResponseType;
(function (RunResponseType) {
    RunResponseType["TEXT"] = "TEXT";
    RunResponseType["STREAM"] = "STREAM";
    RunResponseType["STRING_GROUP"] = "STRING_GROUP";
    RunResponseType["IMAGE"] = "IMAGE";
    RunResponseType["IMAGE_GROUP"] = "IMAGE_GROUP";
    RunResponseType["STOCK"] = "STOCK";
    RunResponseType["AMAZONSHOPPING"] = "AMAZONSHOPPING";
    RunResponseType["WEEESHOPPING"] = "WEEESHOPPING";
    RunResponseType["JDSHOPPING"] = "JDSHOPPING";
    RunResponseType["YOUTUBE_VIDEOS"] = "YOUTUBE_VIDEOS";
    RunResponseType["WEEEGRPCERIES"] = "WEEEGRPCERIES";
    RunResponseType["MAP"] = "MAP";
    RunResponseType["TUTORIAL"] = "TUTORIAL";
    RunResponseType["FOOD"] = "FOOD";
    RunResponseType["ASK_BRAIN"] = "ASK_BRAIN";
    RunResponseType["PROTEIN"] = "PROTEIN";
    RunResponseType["MIDJOURNEY"] = "MIDJOURNEY";
    RunResponseType["ERROR"] = "ERROR";
    RunResponseType["CHAT_INTERFACE"] = "CHAT_INTERFACE";
    RunResponseType["BLUEPRINT"] = "BLUEPRINT";
    RunResponseType["CUSTOM_API"] = "CUSTOM_API";
    RunResponseType["CUSTOM_FUNCTION"] = "CUSTOM_FUNCTION";
    RunResponseType["INTERVIEW_BOT"] = "INTERVIEW_BOT";
})(RunResponseType || (RunResponseType = {}));
export class RunResponse {
    constructor(type, data) {
        this.type = type;
        this.data = data;
    }
}
